// Shared constants for Haklai calculator

// Haklai money inputs stop at 9 digits. "999,999,999" is 77px at 13.6px
// Rubik; the pill adds 8px of padding on each side and the outline.
export const MAX_MONEY_DIGITS = 9;
export const MAX_MONEY_AMOUNT = 999999999;
export const HAKLAI_MONEY_INPUT_WIDTH = 104;

// ── Category identifiers for property parts ──────────────────────────
export const CATEGORIES = {
  RESIDENTIAL: "residential",
  COMMERCIAL: "commercial",
  COMMERCIAL_BG: "commercialBg",
  LAND_AND_SHARED: "landAndShared",
  FARM_REMAINDER: "farmRemainder"
};
export const HAKLAI_FARM_REMAINDER_KEY = "farmRemainder_1";
export const HAKLAI_FARM_REMAINDER_LABEL = "יתרת המשק";
// ── Residential house labels (index = slot number - 1) ───────────────
export const RESIDENTIAL_LABELS = [
  "בית מגורים ראשי",
  "בית מגורים שני",
  "בית מגורים שלישי",
  "בית מגורים רביעי"
];

export const MAX_RESIDENTIAL = 4;

// ── Commercial asset type options ─────────────────────────────────────
export const COMMERCIAL_OPTIONS = [
  { key: "warehouse",          label: "מחסן" },
  { key: "shed",               label: "סככה" },
  { key: "greenhouse",         label: "חממה" },
  { key: "poultryHouse",       label: "לול" },
  { key: "cowshed",            label: "רפת" },
  { key: "stable",             label: "אורווה" },
  { key: "packingHouse",       label: "בית אריזה" },
  { key: "processingBuilding", label: "מבנה עיבוד" },
  { key: "officeBuilding",     label: "בניין משרד" },
  { key: "workshop",           label: "בית מלאכה / סדנה" },
  { key: "equipmentStorage",   label: "מבנה אחסון לציוד" },
  { key: "coldStorage",        label: "מבנה קירור" },
  { key: "store",              label: "חנות" },
  { key: "winery",             label: "יקב" },
  { key: "dairy",              label: "מחלבה / מגבנה" },
  { key: "olivePress",         label: "בית בד" },
  { key: "nursery",            label: "משתלה" }
];

// ── Predefined land & shared options retained for legacy label resolution ──
export const LAND_AND_SHARED_OPTIONS = [
  { key: "openLand",                  label: "קרקע חקלאית (חלקה ב׳)" },
  { key: "poultryLivestockBuildings", label: "מבני עופות / בע\"ח" },
  { key: "processingFacilities",      label: "מתקני עיבוד" },
  { key: "storagePacking",           label: "אחסון ואריזה" },
  { key: "advancedCultivation",       label: "גידול מתקדם" },
  { key: "unutilizedBuildingRights",  label: "זכויות בנייה לא מנוצלות" },
  { key: "communalShare",            label: "חלק משותף (חלקה ג׳)" }
];

export const ZABAN_ATTRIBUTION_OPTIONS = [
  { value: "residence", label: "דירה" },
  { value: "zaban", label: "זב\"ן" },
  { value: "both", label: "גם וגם" }
];

export const HAKLAI_ADD_MODES = {
  ADD: "add",
  UNAVAILABLE: "unavailable"
};

export const HAKLAI_PTOR_TYPES = {
  NONE: "none",
  SINGLE: "single",
  INHERITANCE: "inheritance"
};

export const COMPACT_CURRENCY_LABELS = {
  newShekel: "₪",
  oldShekel: "שקל",
  lira: "לירה",
  usd: "$"
};


export const USD_SUPPORT_START_DATE = "1977-10-31";
export const OLD_SHEKEL_START_DATE = "1980-02-24";
export const NEW_SHEKEL_START_DATE = "1985-09-04";

// ── Domain-date boundaries for legacy tax regimes ─────────────────────────
// Estate tax (מס עזבון) was abolished in Israel on 1981-04-01. For inheritances
// whose testator passed on/after this date, the cost basis transitions from
// the passing-date value to the testator's original acquisition date/cost.
// Used to decide whether to ask for "testator's purchase date" on inheritance
// forms and which currency/amount label to display.
export const INHERITANCE_TESTATOR_BASIS_START_DATE = "1981-04-01";
export const PRE_1997_CONSTRUCTION_CUTOFF_DATE = "1997-04-01";

// The Rabinovich discount (§48א(ד1)) applies to sales whose acquisition date
// falls STRICTLY between these two boundaries — i.e. acquisitions in
// [2001-11-07 .. 2003-12-31]. Exposed as two exclusive bookends to match the
// existing moment().isBefore/isAfter call sites 1:1 (no semantic change).
export const RABINOVICH_WINDOW_START_EXCLUSIVE = "2001-11-06";
export const RABINOVICH_WINDOW_END_EXCLUSIVE = "2004-01-01";

const LOCAL_CURRENCY_OPTIONS = {
  newShekel: { label: "שקל חדש", value: "newShekel" },
  oldShekel: { label: "שקל ישן", value: "oldShekel" },
  lira: { label: "לירה", value: "lira" },
  usd: { label: "דולר", value: "usd" }
};

const getHaklaiLocalCurrencyForDate = (date) => {
  if (!date || date >= NEW_SHEKEL_START_DATE) return LOCAL_CURRENCY_OPTIONS.newShekel.value;
  if (date >= OLD_SHEKEL_START_DATE) return LOCAL_CURRENCY_OPTIONS.oldShekel.value;
  return LOCAL_CURRENCY_OPTIONS.lira.value;
};

const isHaklaiUsdSupportedForDate = (date) => !date || date >= USD_SUPPORT_START_DATE;

export const getHaklaiCurrencyOptions = (date) => {
  if (!date) {
    return [LOCAL_CURRENCY_OPTIONS.newShekel, LOCAL_CURRENCY_OPTIONS.usd];
  }

  const localCurrency = getHaklaiLocalCurrencyForDate(date);
  const options = [LOCAL_CURRENCY_OPTIONS[localCurrency]];
  if (isHaklaiUsdSupportedForDate(date)) {
    options.push(LOCAL_CURRENCY_OPTIONS.usd);
  }
  return options;
};

export const normalizeHaklaiCurrencyForDate = (date, currentCurrency) => {
  if (!date) {
    return currentCurrency === LOCAL_CURRENCY_OPTIONS.usd.value || currentCurrency === LOCAL_CURRENCY_OPTIONS.newShekel.value
      ? (currentCurrency || LOCAL_CURRENCY_OPTIONS.newShekel.value)
      : LOCAL_CURRENCY_OPTIONS.newShekel.value;
  }

  if (currentCurrency === LOCAL_CURRENCY_OPTIONS.usd.value && isHaklaiUsdSupportedForDate(date)) {
    return LOCAL_CURRENCY_OPTIONS.usd.value;
  }

  return getHaklaiLocalCurrencyForDate(date);
};
